import { getAccessToken } from "../auth/msal/helpers";

type StreamChunk = {
  token?: string;
  isComplete?: boolean;
  messageID?: string;
};

type ChatResponse = {
  response: string;
  messageID?: string;
  cancelled?: boolean;
};

class CancelledError extends Error {}

const WS_URL = process.env.CACTAI_URL ?? "";

let socket: WebSocket | null = null;
let connecting: Promise<WebSocket> | null = null;
let generation = 0;
let cancelActive: (() => void) | null = null;

/** Snapshot before starting a request; cancelMessage() invalidates every earlier snapshot. */
export function getGeneration(): number {
  return generation;
}

function createAuthorizedWebSocket(url: string, token: string): WebSocket {
  // const WS = WebSocket as unknown as WSWithHeaders;
  // return new WS(url, [], {
  //   headers: { Authorization: `Bearer ${token}` },
  // });
  const urlObj = new URL(url);
  urlObj.searchParams.set("Authorization", `Bearer ${token}`);
  return new WebSocket(urlObj.toString());
}

/**
 * HTTP → WebSocket upgrade ($connect) with Authorization on the upgrade request.
 */
export async function connect(): Promise<WebSocket> {
  if (socket?.readyState === WebSocket.OPEN) return socket;
  if (connecting) return connecting;

  const connectGeneration = generation;

  connecting = (async () => {
    if (!WS_URL) {
      throw new Error("CACTAI_URL is not configured");
    }

    const token = await getAccessToken();
    if (!token) throw new Error("No access token for upgrade");

    const url = new URL(WS_URL);
    if (url.protocol !== "wss:" && url.protocol !== "ws:") {
      throw new Error(`CACTAI_URL must be wss:// (got ${url.protocol})`);
    }

    console.log("[CactAI WS] upgrading", url.toString());

    const ws = createAuthorizedWebSocket(url.toString(), token);

    await new Promise<void>((resolve, reject) => {
      const onOpen = () => {
        cleanup();
        console.log("[CactAI WS] upgrade complete ($connect ok)");
        resolve();
      };
      const onError = () => {
        cleanup();
        reject(new Error("WebSocket upgrade failed"));
      };
      const onClose = (event: CloseEvent) => {
        console.log("[CactAI WS] upgrade closed", {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
        });
        cleanup();
        reject(
          new Error(event.reason || `WebSocket upgrade closed (${event.code})`),
        );
      };

      const onMessage = (event: MessageEvent) => {
        console.log("[CactAI WS] message", event.data);
      };

      const cleanup = () => {
        ws.removeEventListener("open", onOpen);
        ws.removeEventListener("error", onError);
        ws.removeEventListener("close", onClose);
        ws.removeEventListener("message", onMessage);
      };

      ws.addEventListener("message", onMessage);
      ws.addEventListener("open", onOpen);
      ws.addEventListener("error", onError);
      ws.addEventListener("close", onClose);
    });

    if (connectGeneration !== generation) {
      ws.close(1000, "cancelled during connect");
      throw new CancelledError();
    }

    ws.addEventListener("close", (event) => {
      console.warn("[CactAI WS] closed", {
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean,
      });
      if (socket === ws) socket = null;
    });

    socket = ws;
    return ws;
  })();

  try {
    return await connecting;
  } catch (err) {
    socket = null;
    throw err;
  } finally {
    connecting = null;
  }
}

export function disconnect(): void {
  const ws = socket;
  socket = null;
  connecting = null;
  if (
    ws &&
    (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)
  ) {
    ws.close(1000, "client disconnect");
  }
}

/**
 * Sends a prompt on the `stream` route and resolves with the full assistant reply.
 */
export async function sendMessage(
  prompt: string,
  startedAt: number = generation,
): Promise<ChatResponse> {
  try {
    if (!prompt.trim()) {
      return { response: "prompt must not be empty" };
    }

    if (startedAt !== generation) throw new CancelledError();

    const ws = await connect();

    if (startedAt !== generation) throw new CancelledError();

    return await new Promise<ChatResponse>((resolve, reject) => {
      let buffer = "";
      let messageID: string | undefined;
      let settled = false;

      const fail = (error: Error) => {
        if (settled) return;
        settled = true;
        cleanup();
        reject(error);
      };

      const succeed = (response: string) => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve({ response, messageID });
      };

      const onMessage = (event: MessageEvent) => {
        try {
          const data = JSON.parse(String(event.data)) as StreamChunk;
          if (typeof data.messageID === "string" && data.messageID.length > 0) {
            messageID = data.messageID;
          }
          if (typeof data.token === "string" && data.token.length > 0) {
            buffer += data.token;
          }
          if (data.isComplete) {
            succeed(buffer);
          }
        } catch {
          fail(new Error("Invalid message from WebSocket"));
        }
      };

      const onError = () => fail(new Error("WebSocket error during stream"));

      const onClose = (event: CloseEvent) => {
        fail(
          new Error(
            event.reason || `WebSocket closed during stream (${event.code})`,
          ),
        );
      };

      const cleanup = () => {
        ws.removeEventListener("message", onMessage);
        ws.removeEventListener("error", onError);
        ws.removeEventListener("close", onClose);
        if (cancelActive === abort) cancelActive = null;
      };

      const abort = () => fail(new CancelledError());
      cancelActive = abort;

      ws.addEventListener("message", onMessage);
      ws.addEventListener("error", onError);
      ws.addEventListener("close", onClose);

      try {
        ws.send(JSON.stringify({ action: "stream", prompt }));
      } catch (err) {
        fail(
          err instanceof Error
            ? err
            : new Error("Failed to send stream message"),
        );
      }
    });
  } catch (error: unknown) {
    if (error instanceof CancelledError) {
      return { response: "", cancelled: true };
    }
    const message =
      error instanceof Error ? error.message : "An unknown error occurred";
    console.log("[CactAI WS] sendMessage failed:", message);
    return { response: message };
  }
}

/**
 * Settles the in-flight request as cancelled, tells the backend, and drops the socket
 * so a late response for the cancelled prompt can never reach a later prompt.
 */
export function cancelMessage(): void {
  generation += 1;
  cancelActive?.();

  const ws = socket;
  if (ws?.readyState === WebSocket.OPEN) {
    try {
      ws.send(JSON.stringify({ action: "cancel" }));
    } catch (error: unknown) {
      console.log("[CactAI WS] cancelMessage send failed:", error);
    }
  }
  disconnect();
}
